import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { minutesToMilliseconds } from "date-fns/minutesToMilliseconds";
import { usePostHog } from "posthog-js/react";
import { useMemo } from "react";
import { getCurrentInventoryYear } from "~components/Resources/ToolAdministration/inventoryUtils.ts";
import { toPostHogTenant } from "~components/shared/postHog/useSetPostHogProperties.ts";
import { useToast } from "~components/shared/useToast.tsx";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext.tsx";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import type {
  Tool,
  ToolFilterOptions,
  ToolInventoryCreateBody,
  ToolInventoryUpdateBody,
  Tools,
} from "~generated";
import { ToolAdministrationService, ToolInventoryService } from "~generated";
import type { BillableTool } from "./BillableTool";
import { getApiErrors, type QueryOpts } from "./helpers";
import { toolKeys } from "./toolKeys";

export interface FilterParams {
  page?: number;
  active?: boolean;
  inventoried?: boolean;
  inventoryYear?: number;
  billable?: boolean;
  categoryId?: string;
  limit?: number;
  searchText?: string;
  serialNumber?: string;
  responsibilityStates?: Tool.responsibility_state[];
  responsibleEmployeeIds?: string[];
  tenantIds?: string[];
  managerIds?: string[];
  withoutResponsibleEmployee?: boolean;
  abbreviations?: string[];
}

interface GetToolsArgs extends FilterParams {
  signal?: AbortSignal;
}

export async function getTools({
  page,
  active,
  billable,
  categoryId,
  limit,
  searchText,
  serialNumber,
  responsibilityStates,
  responsibleEmployeeIds,
  tenantIds,
  managerIds,
  withoutResponsibleEmployee,
  abbreviations,
}: GetToolsArgs): Promise<Tools> {
  const promise = ToolAdministrationService.getTools(
    page,
    active,
    billable,
    categoryId,
    limit,
    searchText,
    serialNumber,
    withoutResponsibleEmployee,
    responsibilityStates,
    responsibleEmployeeIds,
    managerIds,
    tenantIds,
    abbreviations,
  );
  const response = await promise;
  if (!response?.tools) {
    return {
      tools: [],
      count: 0,
      available_count: 0,
      assigned_count: 0,
      taken_count: 0,
      next_page: null,
    };
  }
  return response;
}

export function useBillableTools(
  filterParams?: FilterParams,
  options?: QueryOpts<Tools, ReturnType<typeof toolKeys.list>>,
) {
  const query = useAllTools(
    { billable: true, active: true, ...filterParams },
    options,
  );

  return useMemo(
    () => ({ ...query, data: query.data?.tools as BillableTool[] }),
    [query],
  );
}

export function useAllTools(
  filterParams: FilterParams,
  options?: QueryOpts<Tools, ReturnType<typeof toolKeys.list>>,
) {
  const searchText = getRelevantSearchText(filterParams);

  return useQuery({
    queryKey: toolKeys.list({ ...filterParams, searchText }),
    queryFn: async () => getTools({ ...filterParams, searchText }),
    placeholderData: keepPreviousData,
    staleTime: minutesToMilliseconds(1),
    ...options,
  });
}

export function useToolFilterOptions(
  filterParams: FilterParams,
  options?: QueryOpts<
    ToolFilterOptions,
    ReturnType<typeof toolKeys.filterOptions>
  >,
) {
  return useQuery({
    queryKey: toolKeys.filterOptions(filterParams),
    queryFn: async () =>
      ToolAdministrationService.getFilterOptions(
        filterParams.active,
        filterParams.billable,
        filterParams.categoryId,
        filterParams.searchText,
        filterParams.serialNumber,
        filterParams.withoutResponsibleEmployee,
        filterParams.responsibilityStates,
        filterParams.responsibleEmployeeIds,
        filterParams.managerIds,
        filterParams.tenantIds,
        filterParams.abbreviations,
      ),
    ...options,
  });
}

export function useInventoryFilterOptions(
  filterParams: FilterParams,
  options?: QueryOpts<
    ToolFilterOptions,
    ReturnType<typeof toolKeys.inventoryFilterOptions>
  >,
) {
  return useQuery({
    queryKey: toolKeys.inventoryFilterOptions(filterParams),
    queryFn: async () =>
      ToolInventoryService.getFilterOptions(
        filterParams.inventoryYear || getCurrentInventoryYear(),
        filterParams.billable,
        filterParams.categoryId,
        filterParams.searchText,
        filterParams.serialNumber,
        filterParams.withoutResponsibleEmployee,
        filterParams.responsibilityStates,
        filterParams.responsibleEmployeeIds,
        filterParams.managerIds,
        filterParams.tenantIds,
        filterParams.abbreviations,
        filterParams.inventoried,
      ),
    ...options,
  });
}

export function getRelevantSearchText(filterParams: FilterParams) {
  return filterParams.searchText && filterParams.searchText.length >= 3
    ? filterParams.searchText
    : undefined;
}

type CreateInventoryMutationArgs = {
  toolId: string;
  images: File[];
  position: ToolInventoryCreateBody["position"];
  thumbnails: File[];
};

export function useCreateInventory() {
  const queryClient = useQueryClient();
  const currentTenant = useCurrentTenant();
  const postHog = usePostHog();
  const tenant = toPostHogTenant(currentTenant);
  const { showSuccessToast, showErrorToast } = useToast();
  const t = useTranslation();

  return useMutation({
    mutationFn: ({
      toolId,
      images,
      position,
      thumbnails,
    }: CreateInventoryMutationArgs) => {
      // The following does not work.
      // return ToolAdministrationService.createInventory(toolId, { files });
      // So we need the following ugly solution with a type cast:
      const formData: Record<string, unknown> = {};
      formData.position = position;
      images.forEach((image, index) => {
        formData[`images[${index}]`] = image;
      });
      thumbnails.forEach((thumbnail, index) => {
        formData[`thumbnails[${index}]`] = thumbnail;
      });
      return ToolAdministrationService.createInventory(
        toolId,
        formData as unknown as ToolInventoryCreateBody,
      );
    },
    onSuccess: async () => {
      postHog.capture("tool inventory created", {
        tenant,
        year: getCurrentInventoryYear(),
      });
      showSuccessToast(t.toolAdministrationInventoryUploadSuccess);
      await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
    },
    onError: (error) => {
      try {
        const errors = getApiErrors(error);
        showErrorToast(errors[0]);
      } catch {
        showErrorToast(t.unknownError);
      }
    },
  });
}

type UpdateInventoryMutationArgs = {
  toolInventoryId: string;
  photoIdsToDelete: string[];
  newImages: File[];
  newThumbnails: File[];
};

export function useUpdateInventory({
  onSuccessCallback,
}: {
  onSuccessCallback?: () => void;
} = {}) {
  const queryClient = useQueryClient();
  const currentTenant = useCurrentTenant();
  const postHog = usePostHog();
  const tenant = toPostHogTenant(currentTenant);
  const { showSuccessToast, showErrorToast } = useToast();
  const t = useTranslation();

  return useMutation({
    mutationFn: async ({
      toolInventoryId,
      photoIdsToDelete,
      newImages,
      newThumbnails,
    }: UpdateInventoryMutationArgs) => {
      const formData: Record<string, unknown> = {};
      newImages.forEach((image, index) => {
        formData[`new_images[${index}]`] = image;
      });
      newThumbnails.forEach((thumbnail, index) => {
        formData[`new_thumbnails[${index}]`] = thumbnail;
      });
      return ToolAdministrationService.updateInventory(
        toolInventoryId,
        formData as unknown as ToolInventoryUpdateBody,
        photoIdsToDelete,
      );
    },
    onSuccess: async () => {
      onSuccessCallback?.();
      postHog.capture("tool inventory updated", {
        tenant,
        toolInventoryYear: getCurrentInventoryYear(),
      });
      showSuccessToast(t.toolAdministrationInventoryUpdateSuccess);
      await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
    },
    onError: () => showErrorToast(t.unknownError),
  });
}

export function useDeleteInventory() {
  const queryClient = useQueryClient();
  const currentTenant = useCurrentTenant();
  const postHog = usePostHog();
  const tenant = toPostHogTenant(currentTenant);
  const { showSuccessToast, showErrorToast } = useToast();
  const t = useTranslation();

  return useMutation({
    mutationFn: async ({ toolInventoryId }: { toolInventoryId: string }) => {
      await ToolAdministrationService.deleteInventory(toolInventoryId);
    },
    onSuccess: async () => {
      postHog.capture("tool inventory deleted", {
        tenant,
        year: getCurrentInventoryYear(),
      });
      showSuccessToast(t.toolAdministrationInventoryDeleteSuccess);
      await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
    },
    onError: () => showErrorToast(t.unknownError),
  });
}

export function useMoveInventory() {
  const queryClient = useQueryClient();
  const currentTenant = useCurrentTenant();
  const postHog = usePostHog();
  const tenant = toPostHogTenant(currentTenant);
  const { showSuccessToast, showErrorToast } = useToast();
  const t = useTranslation();

  return useMutation({
    mutationFn: async ({
      toolInventoryId,
      toToolId,
    }: {
      toolInventoryId: string;
      toToolId: string;
    }) => {
      await ToolAdministrationService.moveInventory(toolInventoryId, toToolId);
    },
    onSuccess: async (_, { toolInventoryId }) => {
      postHog.capture("tool inventory moved", {
        tenant,
        toolInventoryId,
        toolInventoryYear: getCurrentInventoryYear(),
      });
      showSuccessToast(t.toolAdministrationInventoryMoveSuccess);
      await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
    },
    onError: () => showErrorToast(t.unknownError),
  });
}

export function useToolInventoryDetail({
  toolInventoryId,
}: {
  toolInventoryId: string | null;
}) {
  return useQuery({
    queryKey: toolKeys.inventoryDetail(toolInventoryId || ""),
    queryFn: async () => {
      return ToolAdministrationService.getInventoryDetail(
        toolInventoryId || "",
      );
    },
    enabled: Boolean(toolInventoryId),
  });
}
export function useInventoryPhoto(photoId: string) {
  return useQuery({
    queryKey: toolKeys.inventoryPhoto(photoId),
    queryFn: async () =>
      ToolAdministrationService.getInventoryPhotoThumbnail(photoId),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

export function useToolPhotoDownloadUrl({ photoId }: { photoId: string }) {
  return useMutation({
    mutationFn: async () => {
      const { download_url } =
        await ToolAdministrationService.downloadPhotoUrl(photoId);
      return download_url;
    },
  });
}
